"use client"

import * as React from "react"
import Link from "next/link"
import Image from "next/image"
import { ArrowRight, Building2, CheckCircle2, Plane, Truck, FileCheck, Globe, TrendingUp, Zap } from "lucide-react"
import { Button } from "@sarkiai/ui"
import { useTranslations } from "@/components/I18nProvider"

const useCaseIds = ["stdsa"] as const

const useCaseIcons: Record<string, { icon: React.ComponentType<{ className?: string }> }[]> = {
  stdsa: [
    { icon: Plane },
    { icon: Truck },
    { icon: FileCheck },
  ],
}

const serviceKeys: Record<string, string[]> = {
  stdsa: ["air", "road", "customs"],
}

export default function UseCasesClient() {
  const t = useTranslations("use_cases_page")

  return (
    <main className="flex min-h-screen flex-col">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-b from-primary/5 to-background px-4 lg:px-12">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-6xl uppercase tracking-tighter leading-none">
              {t("hero.title")}
            </h1>
            <p className="text-xl text-muted-foreground">
              {t("hero.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases Grid */}
      <section className="py-16 md:py-24 px-4 lg:px-12">
        <div className="container mx-auto">
          <div className="grid gap-8 md:gap-12">
            {useCaseIds.map((caseId) => {
              const results = t.raw(`cases.${caseId}.results`) as string[]
              const icons = useCaseIcons[caseId] || []
              const svcKeys = serviceKeys[caseId] || []

              return (
                <Link
                  key={caseId}
                  href={`/use-cases/${caseId}`}
                  className="group relative overflow-hidden rounded-3xl border bg-card transition-all hover:shadow-2xl hover:border-primary/20"
                >
                  <div className="grid md:grid-cols-2 gap-8 p-8 md:p-12">
                    {/* Content */}
                    <div className="space-y-6">
                      <div className="flex flex-wrap gap-2">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                          <Building2 className="h-4 w-4" />
                          {t(`cases.${caseId}.industry`)}
                        </div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm font-medium">
                          <Globe className="h-4 w-4" />
                          {t(`cases.${caseId}.location`)}
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 relative rounded-2xl bg-white p-2 shadow-sm shrink-0 border border-border/50">
                          <Image
                            src={`/assets/use-cases/${caseId}/logo.png`}
                            alt={t(`cases.${caseId}.title`)}
                            fill
                            className="object-contain p-2"
                          />
                        </div>
                        <div className="space-y-1">
                          <h2 className="text-3xl md:text-4xl uppercase tracking-tighter leading-none group-hover:text-primary transition-colors">
                            {t(`cases.${caseId}.title`)}
                          </h2>
                          <p className="text-sm uppercase tracking-widest text-primary">
                            {t(`cases.${caseId}.subtitle`)}
                          </p>
                        </div>
                      </div>

                      <p className="text-muted-foreground leading-relaxed prose prose-sm max-w-none">
                        {t(`cases.${caseId}.description`)}
                      </p>

                      <div className="space-y-3">
                        <h3 className="text-lg uppercase tracking-tight flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-primary" />
                          {t("measured_impact")}
                        </h3>
                        <ul className="grid sm:grid-cols-2 gap-2">
                          {results.map((result: string, idx: number) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                              {result}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <Button className="group/btn h-12 px-8 rounded-2xl uppercase tracking-widest text-xs">
                        {t("read_case_study")}
                        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                      </Button>
                    </div>

                    {/* Services display */}
                    <div className="relative flex flex-col justify-center gap-4 bg-muted/30 p-8 rounded-2xl border border-dashed border-border/50">
                      <h3 className="text-xs uppercase tracking-widest opacity-60 mb-2">
                        {t("covered_services")}
                      </h3>
                      <div className="grid gap-3">
                        {svcKeys.map((svcKey, idx) => {
                          const IconComp = icons[idx]?.icon
                          return (
                            <div key={svcKey} className="flex items-center gap-4 p-4 rounded-xl bg-background border shadow-sm group-hover:border-primary/20 transition-colors">
                              <div className="w-10 h-10 rounded-lg bg-primary/5 flex items-center justify-center">
                                {IconComp && <IconComp className="h-5 w-5 text-primary" />}
                              </div>
                              <span className="uppercase tracking-tight text-sm">
                                {t(`cases.${caseId}.services.${svcKey}`)}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                      <p className="text-xs text-muted-foreground font-medium mt-4 flex items-center gap-2 italic">
                        <Zap className="h-3 w-3 text-yellow-500" />
                        {t("also_includes")}
                      </p>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-32 bg-primary text-primary-foreground rounded-4xl mx-4 lg:mx-12 mb-12 overflow-hidden relative">
        <div className="absolute inset-0 bg-[url('/assets/grid.svg')] opacity-10" />
        <div className="container relative z-10 mx-auto text-center space-y-8">
          <h2 className="text-4xl md:text-7xl uppercase tracking-tighter leading-none">
            {t("cta.title")}
          </h2>
          <p className="text-xl opacity-90 max-w-2xl mx-auto leading-relaxed">
            {t("cta.subtitle")}
          </p>
          <Button size="lg" variant="secondary" className="h-16 px-10 rounded-2xl text-xs uppercase tracking-widest" asChild>
            <Link href="/contact">
              {t("cta.button")}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </main>
  )
}
